<template>
  <div class="basketMobile" :class="{ open: cartStore?.isBasketOpen }">
    <div ref="wrapperRef" class="basketMobile__wrapper">
      <!-- Заголовок корзины -->
      <BasketHeader v-if="hasItems" @close="handleCloseBasket" />

      <!-- ✅ Контент корзины показывается только когда есть товары -->
      <template v-if="hasItems">
      <div class="basketMobile__content scrollCust">
        <!-- Баннер «Оплата временно приостановлена» (провайдер «Не выбран» в админке) -->
        <PaymentPausedBanner class="mb-4 rounded-xl overflow-hidden" />

        <!-- Метод получения заказа -->
        <MethodReceipt
          :user-data="basketForm.getUserDataForMethodReceipt()"
          :delivery-address-error="basketForm.getDeliveryAddressError()"
          class="mb-4"
          @update-user-data="basketForm.handleReceiptUpdate"
        />

        <!-- Время предзаказа -->
        <BasketPreorderTime class="mb-4" />

        <!-- Очистить корзину (перед товарами, с подтверждением — чтобы исключить случайные касания) -->
        <div class="flex justify-end mb-3">
          <UiKitButton variant="light" color="danger" size="sm" @click="showClearConfirm = true">
            <template #leading>
              <UIcon name="i-heroicons-trash" class="w-4 h-4" />
            </template>
            Очистить корзину
          </UiKitButton>
        </div>

        <!-- Список товаров -->
        <BasketProductList :items="basket.cartItems" />

        <!-- Слайдер выгод: минимум заказа, бесплатная доставка и оба подарка
             (за сумму и за частоту) — одной лентой, а не блоками врозь. -->
        <BasketOrderInfoCards
          :cart-amount="basket.cartTotal"
          :min-order-amount="basket.minOrderAmount"
          :delivery-rules="basket.deliveryRules as any"
          :is-delivery="basket.isDeliveryOrder.value"
        />

        <!-- Рекомендации товаров -->
        <BasketRecommendations :cart-items="cartStore?.cart || []" />

        <!-- Количество приборов -->
        <BasketNumberKitchenAppliances v-model:cutlery="checkoutFormStore.OrderForm.cutlery" />

        <!-- Комментарий к заказу -->
        <BasketComment
          :comment="checkoutFormStore.OrderForm.comment || ''"
          :is-show-comment="basketForm.isShowComment.value"
          @update:comment="(val) => checkoutFormStore.OrderForm.comment = val"
          @update:is-show-comment="(val) => basketForm.isShowComment.value = val"
        />

        <!-- Промокод -->
        <BasketPromocode
          v-model="isPromocodeExpanded"
          v-model:promocode="promocodeLogic.promocode.value"
          :is-applied="promocodeLogic.isApplied.value"
          :is-loading="promocodeLogic.isLoading.value"
          :error="promocodeLogic.error.value"
          :discount-amount="promocodeLogic.discountAmount.value"
          :is-free-delivery="promocodeLogic.isFreeDelivery.value"
          :incompatible-with-loyalty="promocodeIncompatibleWithLoyalty"
          :onboarding-suppressed-discount="onboardingSuppressedDiscount"
          @apply="handleApplyPromocode"
          @remove="handleRemovePromocode"
        />

        <!-- Программа лояльности -->
        <!-- ✅ Скрываем блок если промокод несовместим с лояльностью -->
        <BasketLoyalty
          v-if="!basket.isPromocodeApplied || basket.isPromocodeCompatibleWithLoyalty"
          v-model:use-loyalty-points="basket.useLoyaltyPoints.value"
          v-model:points-to-use="basket.pointsToUse.value"
          :is-loyalty-enabled="!!basket.isLoyaltyEnabled.value"
          :available-points="basket.formattedLoyaltyInfo.value.availablePoints"
          :max-allowed-points="basket.maxPointsToUse.value"
          :onboarding-available="basket.onboardingInfo.value.available"
          :onboarding-discount="basket.onboardingInfo.value.discount"
          :redeem-restriction="basket.pointsRedeemRestriction.value"
          :max-redeem-percent="basket.maxRedeemPercent.value"
          :locked-points="basket.formattedLoyaltyInfo.value.lockedPoints"
          @points-input="basket.handlePointsInput"
          @set-max-points="basket.setMaxPoints"
          @toggle-loyalty="handleLoyaltyToggle"
        />

        <!-- Итоговые суммы — скроллятся вместе с остальным контентом -->
        <BasketSummary
          :price-breakdown="basket.priceBreakdown.value"
          :formatted-loyalty-info="basket.formattedLoyaltyInfo.value"
          :is-loyalty-enabled="!!basket.isLoyaltyEnabled.value"
        />

      </div>

      <!-- Закреплённый подвал: только итог к оплате + кнопка оплаты всегда видны (размытый фон) -->
      <div class="basketMobile__footer">
        <!-- Подвал с кнопкой заказа -->
        <BasketFooter
          :final-amount="basket.finalAmount.value"
          :is-loading="basketForm.loader.value"
          :is-disabled="basket.isOrderDisabled.value"
          :tooltip-text="basket.tooltipText.value"
          :button-label="basket.orderButtonLabel.value"
          @place-order="handlePlaceOrder"
        />
      </div>
      </template>

      <!-- Пустая корзина -->
      <div v-else class="basketMobile__empty" data-testid="cart-empty">
        <div class="basketMobile__empty-icon">
          <UIcon name="i-heroicons-shopping-cart" class="text-6xl text-gray-400 dark:text-gray-600" />
        </div>
        <h3 class="basketMobile__empty-title">Ваша корзина пуста</h3>
        <p class="basketMobile__empty-text">Добавьте товары из меню, чтобы оформить заказ</p>
      </div>
    </div>
  </div>

  <!-- Плавающая кнопка корзины -->
  <UiButtonMain
    v-if="hasItems"
    class="basket-floating-button bg-red-600 dark:bg-red-700"
    size="xl"
    color=""
    :label="`${finalAmount} ₽`"
    @click="handleOpenBasket"
  >
    <!-- Тележка «подпрыгивает» на каждое изменение корзины (кадры icon-bump
         в assets/css/icons.scss). Слотом, а не пропом `icon`: проп рендерит
         иконку CSS-маской. -->
    <template #leading>
      <span ref="cartIconRef" class="inline-flex">
        <UIcon name="i-heroicons-shopping-cart-solid" class="w-6 h-6" />
      </span>
    </template>
  </UiButtonMain>

  <!-- Подтверждение очистки корзины -->
  <BasketClearConfirm v-model="showClearConfirm" @confirm="handleClearCart" />
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useCartStore } from '@entities/order';
import { useCheckoutFormStore, useBasketForm, useBasketActions, usePromocode, useBasket, useBasketState } from '@features/basket';
import { useModalStore, modalStateEnum } from '@shared/model';
import { useKitToast } from '@shared/ui/kit';



import { useDeliveryRulesStorage, useAppearance } from '@entities/institution';
import { LAYOUT_BASKET_COLUMN_BREAKPOINT } from '@shared/config';
import { useBackButton } from '@shared/lib';

import BasketHeader from '~/widgets/basket/ui/BasketHeader.vue';
import BasketPromocode from '~/widgets/basket/ui/BasketPromocode.vue';
import BasketLoyalty from '~/widgets/basket/ui/BasketLoyalty.vue';
import BasketSummary from '~/widgets/basket/ui/BasketSummary.vue';
import BasketFooter from '~/widgets/basket/ui/BasketFooter.vue';
import BasketComment from '~/widgets/basket/ui/BasketComment.vue';
import BasketRecommendations from '~/widgets/basket/ui/BasketRecommendations.vue';

import BasketClearConfirm from '~/widgets/basket/ui/BasketClearConfirm.vue';

// Stores
const cartStore = useCartStore();
const checkoutFormStore = useCheckoutFormStore();
const modalStore = useModalStore();
const { useBackButtonHandler } = useBackButton();
const deliveryRulesStorage = useDeliveryRulesStorage();

// Флаг для отслеживания, была ли показана модалка в текущей сессии
const deliveryRulesShown = ref(false);

// ✅ НОВАЯ АРХИТЕКТУРА: Используем новый useBasket() - единая точка входа
// Принцип: Dependency Inversion - компонент зависит от абстракции (useBasket), а не от деталей
const basket = useBasket();

const basketStateStore = useBasketState();

// Старый usePromocode временно оставляем для валидации (TODO: переместить логику в useBasket)
const promocodeLogic = usePromocode();

// ✅ Ref для управления раскрытием промокода
const isPromocodeExpanded = ref(false);

// Подтверждение очистки корзины (модалка вместо мгновенной очистки)
const showClearConfirm = ref(false);

const basketForm = useBasketForm();
const basketActions = useBasketActions();
const { toast } = useKitToast();

// ✅ Computed: Надежная проверка наличия товаров (двойная проверка)
// ⚠️ ВАЖНО: basket.cartItemsCount и basket.cartItems - это ComputedRef, поэтому используем .value
const hasItems = computed(() => {
  const count = basket.cartItemsCount.value;
  const items = basket.cartItems.value;
  return count > 0 && items.length > 0;
});

const isBasketOpen = computed(() => Boolean(cartStore?.isBasketOpen));
const finalAmount = computed(() => basket?.finalAmount?.value ?? 0);

/**
 * Толчок тележки на плавающей кнопке при каждом изменении корзины.
 *
 * Перезапускаем анимацию вручную, а не через `:key`: иконка лежит в слоте
 * UiButtonMain → UButton, и смена ключа там узел не пересоздаёт — проверено,
 * элемент остаётся тем же и кадры не играют повторно. Классический приём:
 * снять класс, прочитать offsetWidth (принудительный рефлоу), вернуть класс.
 */
const cartIconRef = ref<HTMLElement | null>(null);
watch(finalAmount, (next, previous) => {
  const el = cartIconRef.value;
  if (!el || next === previous) return;
  el.classList.remove('icon-bump');
  void el.offsetWidth;
  el.classList.add('icon-bump');
});

// Взаимоисключение промокода с баллами/скидкой — данные для подсказок в блоке промокода.
// Промокод применён и помечен несовместимым со списанием баллов.
const promocodeIncompatibleWithLoyalty = computed(
  () => promocodeLogic.isApplied.value && !promocodeLogic.isCompatibleWithLoyalty.value,
);
// Приветственная скидка, которую гасит применённый промокод (0 — гасить нечего).
const onboardingSuppressedDiscount = computed(() =>
  basket.onboardingInfo.value.suppressedByPromocode ? basket.onboardingInfo.value.discount : 0,
);

// ============================================
// 📐 ДИНАМИЧЕСКАЯ ВЫСОТА ЛИПКОГО САЙДБАРА (десктоп)
// ============================================
// Высоту считаем от ФАКТИЧЕСКОЙ позиции враппера в вьюпорте (getBoundingClientRect().top),
// а не из суммы статичных слагаемых (высота шапки + хардкод padding + баннер) — они между собой
// не согласованы и дают то overflow (футер уезжал за экран), то провал снизу. При таком подходе
// низ корзины всегда фиксирован на 30px над краем экрана независимо от баннеров/паддингов/контента.
const wrapperRef = ref<HTMLElement | null>(null);
let basketHeightRaf = 0;
let basketResizeObserver: ResizeObserver | null = null;

/**
 * С какой ширины корзина становится колонкой — зависит от пресета раскладки
 * («Внешний вид → Раскладка страницы»). Порог берём из общей константы: тот же
 * набор чисел стоит в медиазапросах assets/css/appearance-layout.scss.
 *
 * `Infinity` для «Без боковых колонок»: там корзина остаётся шторкой на любом экране,
 * и считать ей высоту липкой колонки нельзя — расчёт вычитает высоту шапки, и под
 * кнопкой «Оформить заказ» появлялся провал в полтораста пикселей.
 */
const { appearance } = useAppearance();
const basketColumnBreakpoint = computed(
  () => LAYOUT_BASKET_COLUMN_BREAKPOINT[appearance.value.layout.grid] ?? Number.POSITIVE_INFINITY,
);

function updateBasketHeight(): void {
  if (typeof window === 'undefined') return;
  const el = wrapperRef.value;
  if (!el) return;

  // Шторка (мобайл и пресет «Без боковых колонок»): высоту не ограничиваем —
  // она тянется во весь экран сама, а скроллит внутренний .basketMobile__content
  if (window.innerWidth < basketColumnBreakpoint.value) {
    el.style.removeProperty('height');
    return;
  }

  const top = el.getBoundingClientRect().top;
  // 30px базовый отступ + 25px запас снизу (по требованию)
  const bottomGap = 0;
  // clientHeight (а не innerHeight) — это видимая высота контента БЕЗ горизонтального скроллбара
  // внизу. Страница даёт горизонтальное переполнение (карусели), скроллбар съедает ~17–25px;
  // с innerHeight футер уезжал бы под него. clientHeight даёт реальную доступную высоту.
  const viewportH = document.documentElement.clientHeight;
  // Высота корзины в «залипшем» состоянии = viewport − высота шапки (та же величина,
  // что и sticky top: var(--app-header-h)). Используем её как ВЕРХНИЙ предел.
  const headerH = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--app-header-h'),
  ) || 144;
  const maxHeight = Math.max(0, viewportH - headerH - bottomGap);
  // ⚠️ Когда корзина «отлипает» (её ячейка грида ушла вверх), top уходит в минус и
  // viewportH − top растёт без предела → враппер раздувается, тянет страницу вниз и
  // получается бесконечный скролл, а футер «к оплате» отрывается от корзины.
  // Поэтому клампим высоту сверху до maxHeight (залипшего размера).
  const next = `${Math.max(0, Math.min(viewportH - top - bottomGap, maxHeight))}px`;
  // Гард от лишних записей и от петли обратной связи с ResizeObserver
  if (el.style.height === next) return;
  el.style.height = next;
}

function scheduleBasketHeightUpdate(): void {
  if (basketHeightRaf) return;
  basketHeightRaf = requestAnimationFrame(() => {
    basketHeightRaf = 0;
    updateBasketHeight();
  });
}

// На сайте корзина — постоянная колонка, «открыть» её нечем: шаг воронки отмечаем,
// как только в ней появился первый товар. На мобильных то же событие шлёт шторка,
// повторов не будет — счётчик визита один на оба пути.
watch(
  hasItems,
  (filled) => {
    if (filled) cartStore?.trackCartOpened?.();
  },
  { immediate: true },
);

// Пресет раскладки приезжает с настройками заведения уже после гидрации, а иногда
// меняется прямо в сессии (админ сохранил — витрина перечитала конфиг). Инлайновую
// высоту, посчитанную для прежнего порога, надо снять или пересчитать.
watch(basketColumnBreakpoint, () => scheduleBasketHeightUpdate());

onMounted(() => {
  if (typeof window === 'undefined') return;
  scheduleBasketHeightUpdate();
  window.addEventListener('scroll', scheduleBasketHeightUpdate, { passive: true });
  window.addEventListener('resize', scheduleBasketHeightUpdate, { passive: true });
  // Баннеры/контент над корзиной меняют её позицию → пересчитываем (rAF + гард защищают от петли)
  if ('ResizeObserver' in window) {
    basketResizeObserver = new ResizeObserver(() => scheduleBasketHeightUpdate());
    basketResizeObserver.observe(document.body);
  }
});

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('scroll', scheduleBasketHeightUpdate);
  window.removeEventListener('resize', scheduleBasketHeightUpdate);
  if (basketResizeObserver) {
    basketResizeObserver.disconnect();
    basketResizeObserver = null;
  }
  if (basketHeightRaf) {
    cancelAnimationFrame(basketHeightRaf);
    basketHeightRaf = 0;
  }
});

/**
 * Обрабатывает размещение заказа
 * ✅ УПРОЩЕНО: Вся логика в basketActions, компонент только вызывает
 * Принцип: Single Responsibility - компонент не знает деталей валидации
 */
async function handlePlaceOrder(): Promise<void> {
  await basketActions.handlePlaceOrderClick(
    basket.validatePointsUsage,
    basket.updateOrderFormWithPoints,
  );
}

/**
 * Обрабатывает открытие корзины
 */
function handleOpenBasket(): void {
  basketActions.openBasket();
}

/**
 * Обрабатывает закрытие корзины
 */
function handleCloseBasket(): void {
  basketActions.closeBasket();
}

/**
 * Обрабатывает очистку корзины.
 *
 * Ждём ответ сервера: у авторизованного корзина живёт там, и молча проглоченная ошибка
 * выглядела бы как «очистилось», а после перезагрузки товары возвращались бы.
 */
async function handleClearCart(): Promise<void> {
  try {
    await basketActions.clearCart();
    // Сбрасываем промокод при очистке корзины
    promocodeLogic.resetPromocode();
  } catch {
    toast({
      color: 'danger',
      title: 'Не удалось очистить корзину',
      description: 'Проверьте соединение и попробуйте ещё раз.',
      timeout: 5000,
    });
  }
}

/**
 * Обрабатывает применение промокода (валидация)
 * ✅ УПРОЩЕНО: Убрана бизнес-логика из компонента
 * Принцип: Single Responsibility - компонент только координирует, логика в composable
 */
async function handleApplyPromocode(): Promise<void> {
  // Передаем текущее состояние баллов из единого источника (basket)
  const useLoyalty = basket.useLoyaltyPoints.value
  const result = await promocodeLogic.validatePromocode(useLoyalty)

  // Если промокод применен успешно, обновляем basket state
  if (result && promocodeLogic.isApplied.value) {
    basket.applyPromocode({
      discount: promocodeLogic.discountAmount.value,
      freeDelivery: promocodeLogic.isFreeDelivery.value,
      compatibleWithLoyalty: promocodeLogic.isCompatibleWithLoyalty.value,
      promocodeId: promocodeLogic.promocode.value,
    })
  }
}

/**
 * Обрабатывает удаление промокода
 * ✅ УПРОЩЕНО: Синхронизируем оба источника (promocodeLogic и basket)
 * Принцип: DRY - не дублируем сброс в разных местах
 */
function handleRemovePromocode(): void {
  promocodeLogic.resetPromocode();
  basket.resetPromocode();
}

/**
 * Обрабатывает включение/выключение баллов лояльности
 * ✅ УПРОЩЕНО: Логика перевалидации остается, но читаем из единого источника
 * Принцип: Single Source of Truth - все из basket
 */
async function handleLoyaltyToggle(useLoyalty: boolean): Promise<void> {
  // Если промокод применен и совместим с баллами - перевалидируем
  if (useLoyalty && basket.isPromocodeApplied && basket.isPromocodeCompatibleWithLoyalty) {
    await promocodeLogic.validatePromocode(true)

    // Обновляем basket state после перевалидации
    if (promocodeLogic.isApplied.value) {
      basket.applyPromocode({
        discount: promocodeLogic.discountAmount.value,
        freeDelivery: promocodeLogic.isFreeDelivery.value,
        compatibleWithLoyalty: promocodeLogic.isCompatibleWithLoyalty.value,
        promocodeId: promocodeLogic.promocode.value,
      })
    }
  }
}

/**
 * Автоматически открывает модалку с правилами доставки при открытии корзины
 * ✅ УПРОЩЕНО: Читаем данные из единого источника (basket)
 * Принцип: Single Source of Truth
 */
watch(
  () => isBasketOpen.value,
  async (isOpen, wasOpen) => {
    // Проверяем что корзина была закрыта и теперь открыта
    if (isOpen && !wasOpen) {
      // Читаем все данные из basket (единый источник)
      const isDelivery = basket.isDeliveryOrder.value
      const hasRules = basket.deliveryRules.value.length > 0
      const hasItemsInCart = hasItems.value // Используем наш computed

      // Проверяем настройки пользователя
      const isModalHidden = await deliveryRulesStorage.isDeliveryRulesHidden()

      // Показываем модалку если все условия выполнены
      if (isDelivery && hasRules && hasItemsInCart && !deliveryRulesShown.value && !isModalHidden) {
        setTimeout(() => {
          modalStore.open(modalStateEnum.DeliveryRules, {
            cartAmount: basket.cartTotal,
            minOrderAmount: basket.minOrderAmount,
            deliveryRules: basket.deliveryRules.value as any,
          })
          deliveryRulesShown.value = true
        }, 500)
      }

      // Загружаем данные лояльности при открытии корзины
      await basket.loadLoyaltyInfo()
    }

    // Сбрасываем флаг при закрытии корзины
    if (!isOpen && wasOpen) {
      deliveryRulesShown.value = false
    }
  },
)

/**
 * 📱 Автоматически закрывает корзину на мобильных устройствах когда она становится пустой
 * ✅ UX: Пользователю не нужно вручную закрывать пустую корзину
 */
watch(
  () => basket.cartItemsCount.value,
  (newCount, oldCount) => {
    // Закрываем корзину только если:
    // 1. Корзина была открыта
    // 2. В корзине были товары (oldCount > 0)
    // 3. Корзина стала пустой (newCount === 0)
    if (isBasketOpen.value && oldCount > 0 && newCount === 0) {
      basketActions.closeBasket();
    }
  },
)

// Регистрируем обработчик системной кнопки "Назад" для закрытия корзины
useBackButtonHandler(() => basketActions.closeBasket(), 10)

// 🔄 СИНХРОНИЗАЦИЯ: Двусторонняя синхронизация promocode между basketState и promocodeLogic
// basket.promocode -> promocodeLogic.promocode (из пуша)
watch(
  basket.promocode, // ✅ Передаем computed напрямую
  (newValue) => {
    const promoStr = String(newValue || '')
    if (promoStr !== promocodeLogic.promocode.value) {
      promocodeLogic.promocode.value = promoStr
    }
  },
  { immediate: true },
)

// promocodeLogic.promocode -> basket.promocode (из UI)
watch(
  promocodeLogic.promocode,
  (newValue) => {
    const promoStr = String(newValue || '')
    if (promoStr !== basket.promocode) {
      basket.promocode = promoStr
    }
  },
)

// 🎯 АВТОМАТИЧЕСКОЕ РАСКРЫТИЕ: Открываем промокод при его установке из пуша
watch(
  basket.promocode,
  (newValue, oldValue) => {
    // Если промокод появился (не был пустым, стал непустым) - раскрываем компонент
    const wasEmpty = !oldValue || oldValue === ''
    const isNotEmpty = newValue && newValue !== ''

    if (wasEmpty && isNotEmpty) {
      isPromocodeExpanded.value = true
    }
  },
)

// 🎯 АВТОМАТИЧЕСКОЕ РАСКРЫТИЕ: Открываем промокод при успешном применении
watch(
  () => promocodeLogic.isApplied.value,
  (newValue, oldValue) => {
    // Если промокод был успешно применён - раскрываем компонент
    if (newValue && !oldValue) {
      isPromocodeExpanded.value = true
    }
  },
)

// 🎁 АВТО-ПРИМЕНЕНИЕ ИЗ ПУША: промокод пришёл из уведомления/диплинка (autoApplyRequested).
// Сами валидируем его (usePromocode при валидном gift_product добавит подарок в корзину)
// и сбрасываем флаг, чтобы это не срабатывало на ручной ввод в поле.
watch(
  () => basketStateStore.autoApplyRequested,
  async (requested) => {
    if (!requested) return
    basketStateStore.autoApplyRequested = false
    isPromocodeExpanded.value = true
    if (promocodeLogic.promocode.value && !promocodeLogic.isApplied.value) {
      await promocodeLogic.validatePromocode(false)
    }
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.basketMobile {
  // Мобайл-оверлей: фикс. высота во весь экран, БЕЗ собственного скролла (overflow-hidden) —
  // внутри скроллит .basketMobile__content, а футер прижат снизу через флекс (как на десктопе).
  // Подложка шторки — белая, а не голубая: sky-300 достался от старой темы и делал
  // корзину единственным цветным экраном витрины. Фирменный цвет ей задаётся из админки
  // («Внешний вид → Фон витрины → Красить шапку, фильтры и корзину»), а не хардкодом.
  @apply relative max-xl:bg-white w-full max-sm:p-3 max-xl:p-5 max-xl:h-[calc(100dvh-var(--safe-area-inset-top))] max-w-[470px] max-xl:fixed max-xl:bottom-0 max-xl:-right-[110%] max-xl:z-50 max-xl:overflow-hidden max-xl:dark:bg-zinc-950;

  /* Добавляем плавную анимацию появления */
  transition: right 0.3s ease;

  &.open {
    @apply right-0;
    transition: right 0.5s ease-in-out;
  }

  &__wrapper {
    @apply xl:sticky;

    // Флекс-колонка на ВСЕХ ширинах: контент скроллит сам (flex-1), футер прижат снизу (flex-shrink-0).
    display: flex;
    flex-direction: column;

    // Мобайл-оверлей (<1280): враппер во всю высоту оверлея. Без overflow:hidden — чтобы фуллбли́д
    // футера (-mx-5/-mb-5) не обрезался; края клипает сам .basketMobile (overflow-hidden).
    @media only screen and (max-width: 1279px) {
      height: 100%;
      min-height: 0;
    }

    // Десктоп: липкий сайдбар во всю видимую область. Точную высоту задаёт JS (updateBasketHeight)
    // от ФАКТИЧЕСКОЙ позиции враппера (getBoundingClientRect().top) — это устойчиво к баннерам,
    // паддингам и любому контенту над корзиной. CSS-calc ниже — лишь SSR/до-JS фолбэк.
    @media only screen and (min-width: 1280px) {
      top: var(--app-header-h, 144px);
      height: calc(100dvh - var(--app-header-h, 144px) - 30px - var(--safe-area-inset-top));
      overflow: hidden;
    }
  }

  &__content {
    // Собственный скролл на всех ширинах (флекс-1 внутри __wrapper); футер — отдельной строкой снизу.
    @apply overflow-x-hidden overflow-y-auto;
    flex: 1 1 0%;
    min-height: 0;
  }

  // Подвал: итог заказа + кнопка оплаты ВСЕГДА видны снизу (отдельная флекс-строка, не перекрывает контент)
  &__footer {
    @apply backdrop-blur-lg flex-shrink-0;

    // Мобайл-оверлей: тянем фон во всю ширину и к нижнему краю (компенсируем p-5 оверлея)
    @apply max-xl:-mx-5 max-xl:-mb-5 max-xl:pb-5;
  }

  // ============================================
  // 🛒 ПУСТАЯ КОРЗИНА
  // ============================================

  &__empty {
    @apply flex flex-col items-center justify-center text-center px-6 py-12;
    min-height: calc(100dvh - 200px - var(--safe-area-inset-top));

    @media only screen and (max-width: 768px) {
      min-height: calc(100dvh - 160px - var(--safe-area-inset-top));
    }
  }

  &__empty-icon {
    @apply mb-6;
    animation: bounce-subtle 2s ease-in-out infinite;
  }

  &__empty-title {
    @apply text-2xl font-bold text-gray-900 dark:text-white mb-3;
  }

  &__empty-text {
    @apply text-base text-gray-600 dark:text-gray-400 max-w-sm;
  }
}

.basket-floating-button {
  @apply xl:hidden fixed bottom-5 right-5 z-10;
}

// Плавная анимация для иконки пустой корзины
@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}
</style>